import {
  Component,
  ElementRef,
  HostListener,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DESCRIPTIONS, NameWithLimitLength } from '@simOn/common/helpers';
import { UploadErrorType } from '@simOn/common/models';
import { LimitInformation } from '@simOn/ui/sim-data-usage';
import { UPLOAD_DOCUMENT_TOKEN } from './upload-document.token';
import { resolveSupportedTypes, toAcceptAttribute, validateUploadFile } from './upload-file-validation';
import { UPLOAD_IMAGE_TOKEN } from './upload-image.token';

let uniqueIdCounter = 0;
@Component({
  selector: 'common-upload-image',
  standalone: true,
  templateUrl: './sim-upload-image.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SimUploadImageComponent
    }
  ],
  encapsulation: ViewEncapsulation.None,
  host: {
    '[id]': 'id'
  }
})
export class SimUploadImageComponent implements ControlValueAccessor {
  private readonly _uploadImageToken = inject(UPLOAD_IMAGE_TOKEN);
  private readonly _uploadDocumentToken = inject(UPLOAD_DOCUMENT_TOKEN);
  readonly blobLimits = input<LimitInformation | undefined>();
  readonly maxSize = input<number>();
  readonly maxNumber = input<number>();
  readonly type = input<'photo' | 'document'>('photo');
  readonly maxFileSize = computed(() => {
    if (this.type() === 'photo') {
      return this.maxSize() || this._uploadImageToken.maxSize;
    }
    return this.maxSize() || this._uploadDocumentToken.maxSize;
  });
  readonly maxFileNumber = computed(() => {
    if (this.type() === 'photo') {
      return this.maxNumber() || this._uploadImageToken.maxUpload;
    }
    return this.maxNumber() || this._uploadDocumentToken.maxUpload;
  });
  readonly uploadDisabled = input(false);
  readonly dragOver = signal<boolean>(false);
  readonly uploadedFiles = input(0);
  readonly id = input(`sim-upload-image-${uniqueIdCounter++}`);
  /** Format labels (e.g. `['SVG']`) narrowing the accepted files; empty = the formats of the `type`'s upload token. */
  readonly supportedFormats = input<string[]>([]);
  /** File types accepted by this area - drives both the file picker's `accept` and the validation of picked files. */
  readonly supportedTypes = computed(() => {
    const formats = this.supportedFormats();
    if (formats.length) return resolveSupportedTypes(formats);
    const token = this.type() === 'photo' ? this._uploadImageToken : this._uploadDocumentToken;
    return resolveSupportedTypes(token.supportedFormats);
  });
  readonly supportedExtensions = computed(() => this.supportedTypes().map(({ fileExtension }) => fileExtension));
  readonly supportedMimeTypes = computed(() => this.supportedTypes().map(({ mimeType }) => mimeType));
  readonly acceptAttribute = computed(() => toAcceptAttribute(this.supportedTypes()));

  readonly file = output<File>();
  readonly uploadError = output<UploadErrorType>();
  readonly fileLoaded = output<string | ArrayBuffer | null>();
  readonly fileWithUrlLoaded = output<{ url: string | ArrayBuffer | null; file: File; key: string }>();
  readonly inputImage = viewChild.required<ElementRef<HTMLInputElement>>('inputImage');

  private _value: string | ArrayBuffer | null = null;
  public get value(): string | ArrayBuffer | null {
    return this._value;
  }
  public set value(value: string | ArrayBuffer | null) {
    this._value = value;
  }

  @HostListener('click')
  upload() {
    if (this.uploadDisabled()) return;
    this.inputImage().nativeElement.click();
  }

  @HostListener('dragover', ['$event'])
  dropOver(event: Event) {
    this.stopPropagation(event);
  }

  @HostListener('dragleave', ['$event'])
  dragLeave(event: Event) {
    this.stopPropagation(event);
  }

  @HostListener('drop', ['$event'])
  dropUpload(event: DragEvent) {
    this.stopPropagation(event);
    if (this.uploadDisabled()) {
      this.dragOver.set(false);
      return;
    }
    if (!event.dataTransfer) return;
    const files = Array.from(event.dataTransfer.files);
    if (this.checkLimitForType(files.length)) return;
    files.forEach((file) => {
      this.processFile(file);
    });
    this.dragOver.set(false);
  }

  imageFromHtmlElement(inputImage: HTMLInputElement) {
    if (!inputImage?.files?.[0]) throw new Error('File not found');

    this.processFile(inputImage.files[0]);
  }

  checkLimitForType(numberOfFiles: number): boolean {
    const limitMessage = this.type() === 'photo' ? DESCRIPTIONS.photoExceededLimit : DESCRIPTIONS.docExceededLimit;

    if (this.uploadedFiles() + numberOfFiles > this.maxFileNumber()) {
      this.uploadError.emit({ message: limitMessage, type: 'error' });
      this.inputImage().nativeElement.value = '';
      return true;
    }
    return false;
  }

  processFile(value: File) {
    const file = new File([value], NameWithLimitLength(value), { type: value.type });
    const error = validateUploadFile(file, {
      supportedTypes: this.supportedTypes(),
      maxSize: this.maxFileSize(),
      blobLimits: this.blobLimits()
    });
    if (error) {
      this.uploadError.emit(error);
      this.inputImage().nativeElement.value = '';
      return;
    }

    const reader = new FileReader();
    this.file.emit(file);
    reader.addEventListener('load', (event: ProgressEvent) => {
      this._value = (event.target as FileReader).result;
      this.onChange(this._value);
      this._value && this.fileLoaded.emit(this._value);
      this.fileWithUrlLoaded.emit({ file: file, url: this._value, key: '' });
      this.inputImage().nativeElement.value = '';
    });

    reader.readAsDataURL(file);
  }

  onTouch = () => {};
  onChange: (value: any) => void = () => {};

  writeValue(obj: any): void {
    this._value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  stopPropagation(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  getFileType(file: File): string {
    if (file.type) {
      return file.type;
    }
    const ext = file.name.split('.');
    return '.' + ext[ext.length - 1];
  }
}
